/**
 * Step I — the one identity choice in STILLSUN.
 *
 * Four cards, each showing the fantasy in one line, the passive, and the three abilities
 * with their real cooldowns from `callings.ts`. Progress is per Calling, so the copy has
 * to be honest that switching is free but starts a different tree.
 *
 * The chosen card is the only one that offers the skill tree: Skills is not a sibling tab
 * any more, it is the inside of this card, which is where the points were earned.
 */

import type { VNode } from 'preact';
import { useState } from 'preact/hooks';
import { CALLINGS, SKILL_TREES, type AbilityDef, type CallingDef } from '@stillsun/shared';
import { CALLING_ICON, Icon, isIconName } from '../icons.js';
import { postCalling } from '../../net/progress-api.js';
import { progressStore } from '../../net/progress-store.js';
import {
  type HubController,
  LEDGER_SILENT,
  Loading,
  Section,
  errText,
  useProgress,
} from './common.js';

export interface CallingProps {
  ctl: HubController;
  nonce: number;
  token: string;
  /** Taking up a Calling walks the rail on to step II — on the click, once. */
  onChosen: () => void;
}

const KEYS = ['Q', 'F', 'X'] as const;

function seconds(ms: number): string {
  const s = ms / 1000;
  return `${s % 1 === 0 ? String(s) : s.toFixed(1)} s`;
}

function Ability(props: { def: AbilityDef; slot: number }): VNode {
  const { def, slot } = props;
  return (
    <li class="hub-ability">
      <span class="hub-ability-glyph">
        {isIconName(def.id) ? <Icon name={def.id} size={22} /> : <Icon name="pip" size={22} />}
      </span>
      <span class="hub-ability-key">{KEYS[slot] ?? ''}</span>
      <div class="hub-ability-body">
        <b>{def.name}</b>
        <span class="hub-ability-meta">
          {seconds(def.cooldownMs)}
          {def.staminaCost > 0 ? ` · ${String(def.staminaCost)} stamina` : ''}
        </span>
        <p>{def.desc}</p>
      </div>
    </li>
  );
}

export function CallingTab(props: CallingProps): VNode {
  const { ctl, token } = props;
  const progress = useProgress(token, props.nonce);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const current = progress.data?.calling ?? 0;
  const unlockedCount = progress.data?.unlockedNodes.length ?? 0;
  const unspent = progress.data?.skillPoints ?? 0;

  const choose = (id: number): void => {
    if (busy || id === current) return;
    setBusy(true);
    setNote('');
    // Optimistic: the cards re-paint on the click, and a refusal puts it back.
    const before = current;
    progressStore.patch({ calling: id });
    // The rail advances from the click rather than from a watcher on `calling`, so a
    // refresh after a raid never yanks the player out of the tab they meant to be in.
    props.onChosen();
    void (async () => {
      try {
        await postCalling(token, id);
        await progressStore.load(token, true);
      } catch (err) {
        progressStore.patch({ calling: before });
        setNote(errText(err, 'The Compact did not record that.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  if (progress.loading && !progress.loaded) {
    return (
      <div class="hub-pane">
        <Loading />
      </div>
    );
  }

  const list = Object.values(CALLINGS) as CallingDef[];

  return (
    <div class="hub-pane">
      {progress.data === null ? <p class="hub-notice">{LEDGER_SILENT}</p> : null}
      {note.length > 0 ? <p class="hub-error">{note}</p> : null}

      <Section
        title="Calling"
        icon="calling"
        hint={
          current === 0
            ? 'Pick one — you can change it between raids'
            : 'Switching is free; each Calling keeps its own tree'
        }
      >
        <div class="hub-callings">
          {list.map((c) => {
            const on = c.id === current;
            const tree = SKILL_TREES[c.id];
            const mine = on ? unlockedCount : 0;
            return (
              <article key={String(c.id)} class={`hub-calling${on ? ' on' : ''}`}>
                <header class="hub-calling-head">
                  {/* The column header the whole card hangs off: one large mark, so four
                      Callings read as four silhouettes before a word is read. */}
                  <span class="hub-calling-mark">
                    <Icon name={CALLING_ICON[c.id] ?? 'calling'} size={52} />
                  </span>
                  <h3>{c.name}</h3>
                  {on ? <span class="hub-tag on">chosen</span> : null}
                </header>
                <p class="hub-calling-tag">{c.tagline}</p>
                <p class="hub-calling-passive">
                  <span class="hub-label">Always</span> {c.passiveDesc}
                </p>
                <ul class="hub-abilities">
                  {/* Six slots on the wire since v3; 3..5 are `undefined` until Phase 2b
                      authors them. An unfilled slot draws nothing and keeps its index, so
                      the slot number beside an ability is still the key that casts it. */}
                  {c.abilities.map((a, i) => (a ? <Ability key={a.id} def={a} slot={i} /> : null))}
                </ul>
                <footer class="hub-calling-foot">
                  <span class="hub-hint">
                    {tree
                      ? `${tree.branches.join(' · ')} — ${String(mine)}/${String(tree.nodes.length)} nodes`
                      : ''}
                  </span>
                  {on ? (
                    <button
                      type="button"
                      class={unspent > 0 ? 'hub-btn hub-btn-amber' : 'hub-btn hub-btn-quiet'}
                      onClick={() => {
                        ctl.setTab('skills');
                      }}
                    >
                      <Icon name={unspent > 0 ? 'points' : 'skills'} />
                      {unspent > 0
                        ? `Spend ${String(unspent)} point${unspent === 1 ? '' : 's'}`
                        : 'Skill tree'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      class="hub-btn hub-btn-amber"
                      disabled={busy || progress.data === null}
                      onClick={() => {
                        choose(c.id);
                      }}
                    >
                      <Icon name={CALLING_ICON[c.id] ?? 'calling'} />
                      {`Take up the ${c.name}`}
                    </button>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
